import { Alert, Button, Drawer, Form, Input } from 'antd';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { useSubmission } from '../../../../../shared/hooks/use-submission';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { ProposalDiff } from './ProposalDiff';
import { approveProposal, rejectProposal, type RateLineProposalQueueView } from '../../../rate-cards.api';
import { formatLineKey } from '../../../policies/rate-line-key';

interface DecideProposalDrawerProps {
  proposal: RateLineProposalQueueView | null;
  onDecided: () => void;
  onClose: () => void;
}

/**
 * Approve or reject, each with a reason. A refusal from the server (not your call to make, the date has passed, an overlap
 * that appeared while it waited) is shown here and nothing is assumed: the row stays in the queue until the server confirms.
 */
export function DecideProposalDrawer({ proposal, onDecided, onClose }: DecideProposalDrawerProps) {
  const [form] = Form.useForm<{ reason: string }>();
  const { error, submitting, submit, clearError } = useSubmission();
  const toast = useToast();

  async function decide(decision: 'approve' | 'reject') {
    if (!proposal) return;
    const { reason } = await form.validateFields();
    const request = decision === 'approve' ? approveProposal : rejectProposal;
    const decided = await submit(
      () => request(proposal.id, reason).then(() => true),
      (err) => apiErrorMessage(err) ?? 'That did not go through. Try again.',
    );
    if (!decided) return;
    toast.success(decision === 'approve' ? 'Price approved' : 'Proposal rejected and returned to its author as a draft');
    form.resetFields();
    onDecided();
    onClose();
  }

  return (
    <Drawer open={proposal !== null} onClose={onClose} afterOpenChange={clearError} title="Decide on this price" placement="right" size={520} destroyOnClose>
      {proposal && (
        <div className="mb-6 flex flex-col gap-3">
          <p className="text-sm text-muted">
            {proposal.rateCardCustomerName ?? 'Standard rate card'} · {formatLineKey(proposal)}
          </p>
          <ProposalDiff proposal={proposal} />
        </div>
      )}
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form form={form} layout="vertical" requiredMark={false} disabled={submitting}>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Say why' }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <div className="flex gap-2">
          <Button type="primary" loading={submitting} onClick={() => decide('approve')}>
            Approve
          </Button>
          <Button danger loading={submitting} onClick={() => decide('reject')}>
            Reject
          </Button>
        </div>
      </Form>
    </Drawer>
  );
}
